<script setup lang="ts">
import {
  iButton,
  iButtonStyles,
  iButtonSizes,
  iButtonTypes,
  iIcon,
} from '@inscribe/signal';
import { OnboardingFlow } from '@/types/onboarding.types';
import {
  OnboardingService,
  OnboardingFlowEventType,
} from '@/shared/services/onboarding/onboarding.service';
import { ref } from 'vue';
let dismissAnimationStarted = ref(false);

/** Props */
const props = defineProps<{
  onboardingFlow: OnboardingFlow;
}>();

/** Handlers */
function dismissIntercom() {
  dismissAnimationStarted.value = true;
  setTimeout(() => {
    OnboardingService.markOnboardingIntercomAsDismissed(props.onboardingFlow);
  }, 300); // we need to wait for the animation to complete to change the flag that would remove the component from the DOM
  OnboardingService.logOnboardingFlowEvent({
    eventType: OnboardingFlowEventType.INTERCOM_ARTICLES_DISMISSED,
  });
}

function openArticle(article: any) {
  window.open(article.link, '_blank');
  OnboardingService.logOnboardingFlowEvent({
    eventType: OnboardingFlowEventType.INTERCOM_ARTICLES_OPENED,
  });
}

defineExpose({
  dismissIntercom,
  openArticle,
  onboardingFlow: props.onboardingFlow,
  iButtonStyles,
  iButtonSizes,
  iButtonTypes,
});
</script>

<template>
  <div
    :class="{ dismissed: dismissAnimationStarted }"
    class="onboarding-intercom-articles d-flex flex-column i-p-10 i-gap-9"
  >
    <div class="header d-flex justify-content-between align-items-center">
      <div class="title i-text-heading-5">
        {{ onboardingFlow?.intercomArticles?.title }}
      </div>
      <i-button
        label="Dismiss"
        :button-style="iButtonStyles.GHOST"
        :size="iButtonSizes.SMALL"
        :type="iButtonTypes.NEUTRAL"
        @button-click="dismissIntercom"
      />
    </div>
    <div class="articles d-flex i-gap-10">
      <button
        class="article d-flex i-radius-2xl i-gap-8 i-p-8"
        v-for="article in onboardingFlow?.intercomArticles?.articles"
        :key="article.title"
        @click="openArticle(article)"
      >
        <div class="image-container">
          <img
            class="image i-radius-md"
            :src="article.imageUrl"
            :alt="article.title"
          />
        </div>
        <div class="content d-flex flex-column i-gap-2">
          <div class="title i-text-button-2">{{ article.title }}</div>
          <div class="description i-text-body-3 flex-grow-1">
            {{ article.description }}
          </div>
          <div class="links">
            <div class="link-container d-flex align-items-center i-gap-2">
              <a
                :href="article.link"
                target="_blank"
                class="i-text-subheading-3"
                >Read Article</a
              >
              <i-icon
                class="icon blue"
                symbol="medium_link_square_1"
                :size="10"
              ></i-icon>
            </div>
          </div>
        </div>
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.onboarding-intercom-articles {
  position: relative;
  background: var(--surface-neutral);
  overflow: hidden;
  min-height: 238px;
  height: 238px;
  transition: all 0.3s ease-in-out;
  opacity: 1;
  border-radius: 32px;

  &.dismissed {
    opacity: 0;
    height: 0;
    min-height: 0;
    padding: 0;
    margin-bottom: 0;
  }

  .article {
    background: var(--base);
    height: 140px;
    width: 100%;
    cursor: pointer;
    border: none;
    text-align: left;

    .title {
      color: var(--text-neutral-bold);
    }

    .description {
      color: var(--text-neutral);
    }

    &:hover {
      background-color: var(--fill-neutral-subtle-hover);
    }

    .image-container {
      min-width: 140px;
      .image {
        width: 140px;
        height: 108px;
        object-fit: cover;
      }
    }

    .links {
      .link-container {
        position: relative;
        color: var(--text-primary-bold);
        width: fit-content;

        &:hover {
          &::after {
            content: '';
            position: absolute;
            right: 0;
            bottom: 0;
            width: 100%;
            height: 1px;
            background-color: var(--text-primary-bold);
          }
        }

        a {
          text-decoration: none;
          color: var(--text-primary-bold);
        }
      }
    }
  }
}
</style>
